import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { api } from "@/lib/api";
import TrendsPanel from "@/components/TrendsPanel";
import { Sparkles, Repeat, Gauge, Library, ArrowUpRight, Crown } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const { status } = useSubscription();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, by_type: { hook: 0, remix: 0, score: 0 }, avg_score: 0 });
  const [recent, setRecent] = useState([]);
  const isPro = !!status?.is_pro;
  const remaining = status?.remaining;

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/library").then((r) => setRecent(r.data.slice(0, 5))).catch(() => {});
  }, []);

  const quick = [
    { to: "/app/hooks", title: "HOOK FORGE", desc: "Generate 5 scroll-stopping openers in seconds.", icon: Sparkles, testid: "quick-hooks" },
    { to: "/app/remix", title: "REMIX LAB", desc: "Paste tired content. Get viral rewrites.", icon: Repeat, testid: "quick-remix" },
    { to: "/app/score", title: "SCORE ENGINE", desc: "Brutally honest 0–100 virality rating.", icon: Gauge, testid: "quick-score" },
    { to: "/app/library", title: "VAULT", desc: "Everything you ever generated, one click away.", icon: Library, testid: "quick-library" },
  ];

  return (
    <div className="p-6 md:p-10 lg:p-12 space-y-10">
      {/* Upgrade Banner — free users only */}
      {!isPro && (
        <Link
          to="/app/pricing"
          data-testid="upgrade-banner"
          className="block brutal-border border-signal-yellow bg-signal-yellow/5 hover:bg-signal-yellow/10 p-5 md:p-6 transition-colors group"
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <Crown size={28} className="text-signal-yellow shrink-0" />
              <div>
                <div className="label-eyebrow text-signal-yellow">// unlock everything</div>
                <div className="heading-display text-xl sm:text-2xl mt-1">
                  UPGRADE TO <span className="text-signal-yellow">PRO</span> — $19/mo
                </div>
                <p className="font-mono text-[11px] text-zinc-400 mt-1">
                  Unlimited generations {typeof remaining === "number" && `· ${remaining}/5 left today`} · voice · all trends · priority queue
                </p>
              </div>
            </div>
            <div className="bg-signal-yellow text-black font-bold uppercase tracking-[0.18em] text-xs px-5 py-3 group-hover:bg-signal-yellow-hover transition-colors">
              Upgrade →
            </div>
          </div>
        </Link>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 border-b border-white/10 pb-8">
        <div>
          <div className="label-eyebrow">// operator console</div>
          <h1 className="heading-display text-4xl sm:text-5xl lg:text-6xl mt-2">
            Welcome back, <span className="text-signal-yellow">{user?.name?.split(" ")[0]}</span>.
          </h1>
          <p className="font-mono text-xs text-zinc-500 mt-3 tracking-wide">
            // engine status: ONLINE · model: claude-sonnet-4.5 · latency: optimal
          </p>
        </div>
        <Link
          to="/app/hooks"
          data-testid="cta-new-generation"
          className="bg-signal-yellow text-black font-bold uppercase tracking-[0.18em] text-xs px-6 py-3 hover:bg-signal-yellow-hover transition-colors self-start"
        >
          New generation →
        </Link>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
        {[
          { label: "Total generations", value: stats.total, testid: "stat-total" },
          { label: "Hooks forged", value: stats.by_type?.hook || 0, testid: "stat-hooks" },
          { label: "Remixes shipped", value: stats.by_type?.remix || 0, testid: "stat-remix" },
          { label: "Avg virality", value: stats.avg_score ? `${stats.avg_score}/100` : "—", testid: "stat-avg" },
        ].map((s) => (
          <div key={s.label} className="bg-[#0A0A0A] p-6" data-testid={s.testid}>
            <div className="label-eyebrow">{s.label}</div>
            <div className="font-mono text-4xl font-bold mt-3">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Quick actions + Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="label-eyebrow">// quick actions</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {quick.map((q) => (
              <Link
                key={q.to}
                to={q.to}
                data-testid={q.testid}
                className="group brutal-border bg-[#141414] p-6 hover:border-signal-yellow transition-colors flex items-start justify-between gap-4"
              >
                <div className="flex-1">
                  <q.icon size={24} className="text-signal-yellow mb-4" />
                  <h3 className="heading-display text-xl">{q.title}</h3>
                  <p className="font-mono text-xs text-zinc-500 mt-2 leading-relaxed">{q.desc}</p>
                </div>
                <ArrowUpRight size={20} className="text-zinc-600 group-hover:text-signal-yellow transition-colors shrink-0" />
              </Link>
            ))}
          </div>
        </div>
        <div className="lg:col-span-1">
          <TrendsPanel
            compact
            onPick={(name) => navigate("/app/hooks", { state: { topic: name } })}
          />
        </div>
      </div>

      {/* Recent */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="label-eyebrow">// recent transmissions</div>
          <Link to="/app/library" data-testid="link-all-library" className="font-mono text-[11px] text-zinc-500 hover:text-signal-yellow uppercase tracking-[0.18em]">view all →</Link>
        </div>
        {recent.length === 0 ? (
          <div className="brutal-border p-10 bg-[#141414] text-center">
            <div className="font-mono text-xs text-zinc-500">// no transmissions yet. fire up the engine.</div>
          </div>
        ) : (
          <div className="space-y-2">
            {recent.map((r) => (
              <div key={r.id} data-testid={`recent-${r.id}`} className="brutal-border bg-[#141414] p-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <span className="font-mono text-[10px] bg-signal-yellow text-black px-2 py-1 uppercase tracking-wider">{r.type}</span>
                  <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">{r.platform}</span>
                  <span className="font-mono text-xs text-zinc-300 truncate">{r.input}</span>
                </div>
                {r.score != null && <span className="font-mono text-xs text-signal-yellow shrink-0">{r.score}/100</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
